// src/pages/Chat.jsx - FIXED: Rate-Limited + No Artificial Delays
import React, { useState, useRef, useEffect, useMemo } from 'react';
import { sendChatQuery, sendChatQueryStreaming } from '../services/chat';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

// Helper to create a new session object
const createNewSession = (idSuffix = '') => {
  const timestamp = Date.now();
  return {
    id: `session-${timestamp}${idSuffix}`,
    title: `New Chat - ${new Date(timestamp).toLocaleDateString([], { month: 'numeric', day: 'numeric', year: '2-digit', hour: '2-digit', minute: '2-digit' })}`,
    messages: [],
    createdAt: timestamp,
  };
};

function Chat() {
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);

  // ═══════════════════════════════════════════════════════════
  // FIX #1: RATE LIMITING STATE
  // ═══════════════════════════════════════════════════════════
  const [lastChatRequestTime, setLastChatRequestTime] = useState(0);
  const MIN_CHAT_INTERVAL = 3000; // 3 seconds between requests

  // Streaming state
  const [streamingContent, setStreamingContent] = useState('');
  const [statusMessage, setStatusMessage] = useState('');

  // Session state
  const [sessions, setSessions] = useState([]);
  const [currentSessionId, setCurrentSessionId] = useState(null);
  
  const [isSessionDropdownOpen, setIsSessionDropdownOpen] = useState(false);
  const sessionDropdownRef = useRef(null);
  const sessionButtonRef = useRef(null);

  // Export menu state
  const [isExportMenuOpen, setIsExportMenuOpen] = useState(false);
  const exportMenuRef = useRef(null);
  const exportButtonRef = useRef(null);

  // --- LOCALSTORAGE & SESSION INITIALIZATION ---
  useEffect(() => {
    let initialSessions = [];
    let initialCurrentId = null;
    try {
      const storedSessions = localStorage.getItem('chatSessions');
      const storedCurrentId = localStorage.getItem('currentChatSessionId');

      if (storedSessions) {
        initialSessions = JSON.parse(storedSessions);
      }

      if (initialSessions.length > 0) {
        if (storedCurrentId && initialSessions.find(s => s.id === storedCurrentId)) {
          initialCurrentId = storedCurrentId;
        } else {
          initialSessions.sort((a, b) => b.createdAt - a.createdAt);
          initialCurrentId = initialSessions[0].id;
        }
      } else {
        const newSession = createNewSession('-initial');
        initialSessions = [newSession];
        initialCurrentId = newSession.id;
      }
    } catch (error) {
      console.error("Error loading sessions from localStorage:", error);
      const newSession = createNewSession('-fallback');
      initialSessions = [newSession];
      initialCurrentId = newSession.id;
    }
    
    setSessions(initialSessions);
    setCurrentSessionId(initialCurrentId);
  }, []);

  // ═══════════════════════════════════════════════════════════
  // FIX #2: DEBOUNCED LOCALSTORAGE WRITES
  // ═══════════════════════════════════════════════════════════
  useEffect(() => {
    if (sessions.length > 0 && currentSessionId) {
      // Debounce localStorage writes to prevent excessive I/O
      const timeoutId = setTimeout(() => {
        try {
          localStorage.setItem('chatSessions', JSON.stringify(sessions));
          localStorage.setItem('currentChatSessionId', currentSessionId);
        } catch (error) {
          console.error("Error saving sessions to localStorage:", error);
        }
      }, 500); // Wait 500ms after last change before saving

      return () => clearTimeout(timeoutId);
    }
  }, [sessions, currentSessionId]);

  // --- DERIVED STATE FOR CURRENT SESSION ---
  const currentSession = useMemo(() => {
    return sessions.find(s => s.id === currentSessionId);
  }, [sessions, currentSessionId]);

  const currentMessages = useMemo(() => {
    return currentSession ? currentSession.messages : [];
  }, [currentSession]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [currentMessages, streamingContent]);

  // --- SESSION MANAGEMENT FUNCTIONS ---
  const handleNewChat = () => {
    const newSession = createNewSession();
    setSessions(prevSessions => [newSession, ...prevSessions].sort((a,b) => b.createdAt - a.createdAt));
    setCurrentSessionId(newSession.id);
    setInput('');
    setIsLoading(false);
    setIsSessionDropdownOpen(false);
  };

  const handleSelectSession = (sessionId) => {
    setCurrentSessionId(sessionId);
    setInput('');
    setIsSessionDropdownOpen(false);
  };

  const handleDeleteSession = (sessionIdToDelete, event) => {
    event.stopPropagation();
    setSessions(prevSessions => {
      const remainingSessions = prevSessions.filter(s => s.id !== sessionIdToDelete);
      if (remainingSessions.length === 0) {
        const newSession = createNewSession('-after-delete-all');
        setCurrentSessionId(newSession.id);
        return [newSession];
      }
      
      if (currentSessionId === sessionIdToDelete) {
        const sortedRemaining = [...remainingSessions].sort((a, b) => b.createdAt - a.createdAt);
        setCurrentSessionId(sortedRemaining[0].id);
      }
      return remainingSessions;
    });
  };
  
  // ═══════════════════════════════════════════════════════════
  // FIX #3: RATE-LIMITED CHAT - No artificial delays
  // ═══════════════════════════════════════════════════════════
  const handleSend = async () => {
    if (!input.trim() || !currentSessionId) return;
    
    // Rate limiting check
    const now = Date.now();
    const timeSinceLastRequest = now - lastChatRequestTime;
    
    if (timeSinceLastRequest < MIN_CHAT_INTERVAL) {
      const waitTime = Math.ceil((MIN_CHAT_INTERVAL - timeSinceLastRequest) / 1000);
      alert(`⏱️ Please wait ${waitTime} seconds before sending another message.`);
      return;
    }
    
    setLastChatRequestTime(now);

    const userMessage = { role: 'user', content: input };
    const sessionToUpdate = sessions.find(s => s.id === currentSessionId);
    if (!sessionToUpdate) return;

    const messagesWithUser = [...sessionToUpdate.messages, userMessage];
    let newTitle = sessionToUpdate.title;
    if (sessionToUpdate.messages.length === 0 && userMessage.content && sessionToUpdate.title.startsWith("New Chat")) {
      newTitle = `Chat: ${userMessage.content.substring(0, 25)}${userMessage.content.length > 25 ? '...' : ''}`;
    }

    const updatedSessionWithUserMsg = { ...sessionToUpdate, messages: messagesWithUser, title: newTitle };
    setSessions(prevSessions => prevSessions.map(s => (s.id === currentSessionId ? updatedSessionWithUserMsg : s)));
    
    const currentInput = input;
    setInput('');
    setIsLoading(true);

    try {
      const recentHistory = messagesWithUser.slice(-6);
      let accumulatedContent = '';

      await sendChatQueryStreaming(currentInput, recentHistory, {
        onStatus: (status) => {
          setStatusMessage(status);
        },
        onChunk: (text) => {
          accumulatedContent += text;
          setStreamingContent(accumulatedContent);
          setStatusMessage('');
        },
        onDone: (result) => {
          const assistantMessage = { role: 'assistant', content: result.content };
          setSessions(prevSessions => prevSessions.map(s =>
            s.id === currentSessionId ? { ...s, messages: [...messagesWithUser, assistantMessage] } : s
          ));
          setStreamingContent('');
          setStatusMessage('');
        },
        onError: (err) => {
          console.error('Streaming error:', err);
          const errorMessage = { role: 'assistant', content: 'Sorry, I encountered an error. Please try again.' };
          setSessions(prevSessions => prevSessions.map(s =>
            s.id === currentSessionId ? { ...s, messages: [...messagesWithUser, errorMessage] } : s
          ));
          setStreamingContent('');
          setStatusMessage('');
        },
      });
    } catch (error) {
      // Fallback: if streaming fails entirely, try non-streaming
      console.error('Streaming failed, falling back:', error);
      try {
        const recentHistory = messagesWithUser.slice(-6);
        const response = await sendChatQuery(currentInput, recentHistory);
        const assistantMessage = { role: response.role, content: response.content };
        setSessions(prevSessions => prevSessions.map(s =>
          s.id === currentSessionId ? { ...s, messages: [...messagesWithUser, assistantMessage] } : s
        ));
      } catch (fallbackError) {
        console.error('Fallback also failed:', fallbackError);
        const errorMessage = { role: 'assistant', content: 'Sorry, I encountered an error. Please try again.' };
        setSessions(prevSessions => prevSessions.map(s =>
          s.id === currentSessionId ? { ...s, messages: [...messagesWithUser, errorMessage] } : s
        ));
      }
      setStreamingContent('');
      setStatusMessage('');
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => { 
    if (e.key === 'Enter' && !e.shiftKey) { 
      e.preventDefault(); 
      handleSend(); 
    } 
  };

  // ═══════════════════════════════════════════════════════════
  // FIX #5: HELPER FUNCTIONS FOR RATE LIMITING UI
  // ═══════════════════════════════════════════════════════════
  const canSendChat = () => {
    const timeSinceLastRequest = Date.now() - lastChatRequestTime;
    return !isLoading && 
           input.trim() && 
           timeSinceLastRequest >= MIN_CHAT_INTERVAL;
  };

  const getTimeUntilNextRequest = () => {
    const timeSinceLastRequest = Date.now() - lastChatRequestTime;
    return Math.max(0, MIN_CHAT_INTERVAL - timeSinceLastRequest);
  };

  // ═══════════════════════════════════════════════════════════
  // FIX #6: RATE-LIMITED QUICK ACTIONS
  // ═══════════════════════════════════════════════════════════
  const handleQuickAction = (question) => {
    const now = Date.now();
    const timeSinceLastRequest = now - lastChatRequestTime;
    
    if (timeSinceLastRequest < MIN_CHAT_INTERVAL) {
      const waitTime = Math.ceil((MIN_CHAT_INTERVAL - timeSinceLastRequest) / 1000);
      alert(`⏱️ Please wait ${waitTime} seconds before asking another question.`);
      return;
    }
    
    setInput(question);
    setTimeout(() => handleSend(), 100); // Small delay for UI update
  };

  // --- EXPORT CHAT ---
  const exportChat = (format) => {
    if (!currentMessages || currentMessages.length === 0) {
      alert("No chat content in the current session to export."); return;
    }
    try {
      const sessionTitle = currentSession?.title || 'AI Sentry Chat';
      const timestamp = new Date().toISOString().split('T')[0];
      let content, blob, filename;
      if (format === 'text') {
        content = currentMessages.map(msg => `${msg.role.toUpperCase()}: ${msg.content}`).join('\n\n');
        blob = new Blob([content], { type: 'text/plain' }); filename = `${sessionTitle}_${timestamp}.txt`;
      } else if (format === 'html') {
        content = `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>${sessionTitle}</title><style>body{font-family:Arial,sans-serif;max-width:800px;margin:2rem auto;padding:1rem;line-height:1.6}.message{margin-bottom:1.5rem;padding:1rem;border-radius:0.5rem}.user{background:#e3f2fd;margin-left:2rem}.assistant{background:#f5f5f5;margin-right:2rem}.role{font-weight:bold;margin-bottom:0.5rem;color:#1976d2}.content{white-space:pre-wrap}</style></head><body><h1>${sessionTitle}</h1><p>Exported on ${new Date().toLocaleString()}</p>`;
        currentMessages.forEach(msg => { content += `<div class="message ${msg.role}"><div class="role">${msg.role.toUpperCase()}</div><div class="content">${msg.content.replace(/</g,'&lt;').replace(/>/g,'&gt;')}</div></div>`; });
        content += '</body></html>'; blob = new Blob([content], { type: 'text/html' }); filename = `${sessionTitle}_${timestamp}.html`;
      }
      const url = URL.createObjectURL(blob); const a = document.createElement('a'); a.href = url; a.download = filename; document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
    } catch (error) { console.error("Export failed:", error); alert("Failed to export chat. Please try again."); }
    setIsExportMenuOpen(false);
  };

  // Click Outside Handlers
  useEffect(() => {
    const handleClickOutsideSession = (event) => {
      if (
        isSessionDropdownOpen &&
        sessionDropdownRef.current && !sessionDropdownRef.current.contains(event.target) &&
        sessionButtonRef.current && !sessionButtonRef.current.contains(event.target)
      ) { setIsSessionDropdownOpen(false); }
    };
    document.addEventListener('mousedown', handleClickOutsideSession);
    return () => { document.removeEventListener('mousedown', handleClickOutsideSession); };
  }, [isSessionDropdownOpen]);

  useEffect(() => {
    const handleClickOutsideExport = (event) => {
      if (
        isExportMenuOpen &&
        exportMenuRef.current && !exportMenuRef.current.contains(event.target) &&
        exportButtonRef.current && !exportButtonRef.current.contains(event.target)
      ) { setIsExportMenuOpen(false); }
    };
    document.addEventListener('mousedown', handleClickOutsideExport);
    return () => { document.removeEventListener('mousedown', handleClickOutsideExport); };
  }, [isExportMenuOpen]);

  return (
    <div className="flex flex-col h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-10">
        <div className="flex items-center justify-between px-6 py-4">
          <div className="flex items-center space-x-4">
            <div className="flex items-center">
              <div className="w-8 h-8 bg-gradient-to-br from-blue-700 to-blue-600 rounded-lg flex items-center justify-center mr-3 shadow-sm">
                <span className="text-white text-sm font-bold">OT</span>
              </div>
              <div>
                <h1 className="text-lg font-bold text-gray-900">AI Sentry</h1>
                <p className="text-xs text-gray-500">Industrial Cybersecurity Intelligence</p>
              </div>
            </div>
          </div>

          <div className="flex items-center space-x-3">
            {/* Session Selector */}
            <div className="relative">
              <button
                ref={sessionButtonRef}
                onClick={() => setIsSessionDropdownOpen(!isSessionDropdownOpen)}
                className="flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <svg className="h-4 w-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M2 5a2 2 0 012-2h7a2 2 0 012 2v4a2 2 0 01-2 2H9l-3 3v-3H4a2 2 0 01-2-2V5z" />
                  <path d="M15 7v2a4 4 0 01-4 4H9.828l-1.766 1.767c.28.149.599.233.938.233h2l3 3v-3h2a2 2 0 002-2V9a2 2 0 00-2-2h-1z" />
                </svg>
                {currentSession?.title || 'Select Chat'}
                <svg className="h-4 w-4 ml-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>

              {isSessionDropdownOpen && (
                <div ref={sessionDropdownRef} className="absolute right-0 mt-2 w-80 bg-white border border-gray-200 rounded-lg shadow-lg z-20 max-h-96 overflow-y-auto">
                  <div className="p-2">
                    <button
                      onClick={handleNewChat}
                      className="w-full flex items-center px-4 py-2 text-sm font-medium text-blue-700 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors mb-2"
                    >
                      <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                      New Chat
                    </button>
                    
                    <div className="border-t border-gray-200 pt-2">
                      {sessions.length === 0 ? (
                        <div className="px-4 py-3 text-sm text-gray-500 text-center">No chat sessions yet</div>
                      ) : (
                        sessions.map(session => (
                          <div
                            key={session.id}
                            className={`flex items-center px-4 py-2 text-sm rounded-lg cursor-pointer transition-colors group ${
                              currentSessionId === session.id ? 'bg-blue-50 text-blue-700' : 'text-gray-700 hover:bg-gray-50'
                            }`}
                            onClick={() => handleSelectSession(session.id)}
                          >
                            <div className="flex-1 truncate">
                              <div className="font-medium truncate">{session.title}</div>
                              <div className="text-xs text-gray-500">
                                {session.messages.length} messages
                              </div>
                            </div>
                            <button
                              onClick={(e) => handleDeleteSession(session.id, e)}
                              className="ml-2 p-1 text-gray-400 hover:text-red-600 transition-colors opacity-0 group-hover:opacity-100"
                              title="Delete session"
                            >
                              <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                              </svg>
                            </button>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Export Button */}
            <div className="relative">
              <button
                ref={exportButtonRef}
                onClick={() => setIsExportMenuOpen(!isExportMenuOpen)}
                disabled={!currentMessages || currentMessages.length === 0}
                className={`flex items-center px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  currentMessages && currentMessages.length > 0
                    ? 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                    : 'text-gray-400 bg-gray-100 border border-gray-200 cursor-not-allowed'
                }`}
              >
                <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                Export
              </button>

              {isExportMenuOpen && (
                <div ref={exportMenuRef} className="absolute right-0 mt-2 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-20">
                  <div className="py-1">
                    <button
                      onClick={() => exportChat('text')}
                      className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      <svg className="h-4 w-4 mr-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      Export as Text
                    </button>
                    <button
                      onClick={() => exportChat('html')}
                      className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      <svg className="h-4 w-4 mr-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                      </svg>
                      Export as HTML
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Chat Area */}
      <div className="flex-1 overflow-y-auto px-6 py-6 bg-gradient-to-b from-gray-50 to-white">
        {currentMessages.length === 0 ? (
          <div className="text-center py-12">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-blue-100 text-blue-700 mb-6">
              <span className="text-3xl">💡</span>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Welcome to AI Sentry</h3>
            <p className="text-gray-600 mb-8 max-w-md mx-auto">
              Ask about security alerts, asset discoveries, policy compliance, or any operational intelligence questions.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <button
                onClick={() => handleQuickAction("What critical security alerts do we have?")}
                disabled={!canSendChat()}
                className="px-4 py-2 text-sm font-medium bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Security Alerts
              </button>
              <button
                onClick={() => handleQuickAction("Show me recent asset discoveries")}
                disabled={!canSendChat()}
                className="px-4 py-2 text-sm font-medium bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Asset Discoveries
              </button>
              <button
                onClick={() => handleQuickAction("Analyze current policy compliance")}
                disabled={!canSendChat()}
                className="px-4 py-2 text-sm font-medium bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Policy Compliance
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            {currentMessages.map((msg, idx) => (
              <div key={`${currentSessionId}-msg-${idx}`} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                {msg.role === 'user' ? (
                  <div className="flex items-end max-w-[80%]">
                    <div className="bg-blue-700 text-white px-4 py-3 rounded-2xl rounded-br-md shadow-sm mr-3">
                      <div className="text-sm whitespace-pre-wrap">{msg.content}</div>
                    </div>
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                      <span className="text-blue-700 text-sm">👤</span>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-start max-w-[80%]">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center mr-3">
                      <span className="text-gray-600 text-sm">💡</span>
                    </div>
                    <div className="bg-white border border-gray-200 px-4 py-3 rounded-2xl rounded-bl-md shadow-sm">
                      <div className="text-sm text-gray-800 prose prose-sm max-w-none">
                        <ReactMarkdown
                          remarkPlugins={[remarkGfm]}
                          components={{
                            h1: ({node, ...props}) => <h1 className="text-lg font-bold text-gray-900 mt-2 mb-2" {...props} />,
                            h2: ({node, ...props}) => <h2 className="text-md font-semibold text-gray-900 mt-2 mb-1" {...props} />,
                            h3: ({node, ...props}) => <h3 className="text-sm font-semibold text-gray-900 mt-1 mb-1" {...props} />,
                            ul: ({node, ...props}) => <ul className="list-disc pl-5 mb-2 space-y-1" {...props} />,
                            ol: ({node, ...props}) => <ol className="list-decimal pl-5 mb-2 space-y-1" {...props} />,
                            p: ({node, ...props}) => <p className="mb-2 last:mb-0" {...props} />,
                            a: ({node, ...props}) => <a className="text-blue-700 hover:text-blue-600 font-medium hover:underline" {...props} />,
                            strong: ({node, ...props}) => <strong className="font-semibold text-gray-900" {...props} />,
                            code: ({node, inline, ...props}) => 
                              inline ? 
                                <code className="px-1.5 py-0.5 bg-gray-100 text-blue-700 rounded text-xs font-medium" {...props} /> : 
                                <code className="block bg-gray-50 p-3 rounded-lg text-xs overflow-x-auto my-2 border" {...props} />,
                            table: ({node, ...props}) => (
                              <div className="overflow-x-auto my-4">
                                <table className="min-w-full divide-y divide-gray-300 border border-gray-300" {...props} />
                              </div>
                            ),
                            thead: ({node, ...props}) => <thead className="bg-gray-50" {...props} />,
                            tbody: ({node, ...props}) => <tbody className="divide-y divide-gray-200 bg-white" {...props} />,
                            tr: ({node, ...props}) => <tr {...props} />,
                            th: ({node, ...props}) => (
                              <th className="px-3 py-2 text-left text-xs font-semibold text-gray-900 border-r border-gray-300 last:border-r-0" {...props} />
                            ),
                            td: ({node, ...props}) => (
                              <td className="px-3 py-2 text-xs text-gray-700 border-r border-gray-200 last:border-r-0" {...props} />
                            ),
                          }}
                        >
                          {msg.content}
                        </ReactMarkdown>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        
        {(isLoading || streamingContent) && (
          <div className="flex items-start justify-start mt-6">
            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center mr-3">
              <span className="text-gray-600 text-sm">💡</span>
            </div>
            <div className="bg-white border border-gray-200 px-4 py-3 rounded-2xl rounded-bl-md shadow-sm max-w-[80%]">
              {streamingContent ? (
                <div className="text-sm text-gray-800 prose prose-sm max-w-none">
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={{
                      h1: ({node, ...props}) => <h1 className="text-lg font-bold text-gray-900 mt-2 mb-2" {...props} />,
                      h2: ({node, ...props}) => <h2 className="text-md font-semibold text-gray-900 mt-2 mb-1" {...props} />,
                      h3: ({node, ...props}) => <h3 className="text-sm font-semibold text-gray-900 mt-1 mb-1" {...props} />,
                      ul: ({node, ...props}) => <ul className="list-disc pl-5 mb-2 space-y-1" {...props} />,
                      ol: ({node, ...props}) => <ol className="list-decimal pl-5 mb-2 space-y-1" {...props} />,
                      p: ({node, ...props}) => <p className="mb-2 last:mb-0" {...props} />,
                      a: ({node, ...props}) => <a className="text-blue-700 hover:text-blue-600 font-medium hover:underline" {...props} />,
                      strong: ({node, ...props}) => <strong className="font-semibold text-gray-900" {...props} />,
                      code: ({node, inline, ...props}) =>
                        inline ?
                          <code className="px-1.5 py-0.5 bg-gray-100 text-blue-700 rounded text-xs font-medium" {...props} /> :
                          <code className="block bg-gray-50 p-3 rounded-lg text-xs overflow-x-auto my-2 border" {...props} />,
                      table: ({node, ...props}) => (
                        <div className="overflow-x-auto my-4">
                          <table className="min-w-full divide-y divide-gray-300 border border-gray-300" {...props} />
                        </div>
                      ),
                      thead: ({node, ...props}) => <thead className="bg-gray-50" {...props} />,
                      tbody: ({node, ...props}) => <tbody className="divide-y divide-gray-200 bg-white" {...props} />,
                      tr: ({node, ...props}) => <tr {...props} />,
                      th: ({node, ...props}) => (
                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-900 border-r border-gray-300 last:border-r-0" {...props} />
                      ),
                      td: ({node, ...props}) => (
                        <td className="px-3 py-2 text-xs text-gray-700 border-r border-gray-200 last:border-r-0" {...props} />
                      ),
                    }}
                  >
                    {streamingContent}
                  </ReactMarkdown>
                </div>
              ) : (
                <div className="flex items-center space-x-3">
                  <div className="flex space-x-1">
                    <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                    <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                    <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                  </div>
                  <span className="text-sm text-gray-700">
                    {statusMessage || 'AI Sentry is thinking...'}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <div className="px-6 py-4 bg-white border-t border-gray-200 rounded-b-lg">
        <div className="flex space-x-4">
          <div className="flex-1">
            <textarea
              rows="2"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Ask AI Sentry about your OT environment..."
              className="w-full resize-none border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              disabled={isLoading || !!streamingContent}
            />
            <div className="mt-2 flex items-center justify-between">
              <div className="text-xs text-gray-500">
                Press Enter to send, Shift+Enter for new line
              </div>
              {/* FIX #7: Visual countdown timer */}
              {lastChatRequestTime > 0 && getTimeUntilNextRequest() > 0 && (
                <div className="text-xs text-orange-600 font-medium">
                  ⏱️ Next request in {Math.ceil(getTimeUntilNextRequest() / 1000)}s
                </div>
              )}
            </div>
          </div>
          <button
            onClick={handleSend}
            disabled={!canSendChat()}
            className={`px-6 py-3 rounded-lg text-sm font-medium transition-colors ${
              !canSendChat()
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                : 'bg-blue-700 text-white hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
            }`}
          >
            {isLoading ? (
              <div className="flex items-center">
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Sending
              </div>
            ) : (
              <div className="flex items-center">
                <svg className="h-4 w-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z" />
                </svg>
                Send
              </div>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default Chat;
